import { Loader2 } from "lucide-react";
import type { AsyncTask } from "@/modern/components/async-tasks/types";
import { getTaskStatusMessage } from "@/modern/components/async-tasks/utils";

/** Sidebar row skeleton for work file "Published Pages" panel — driven by AsyncTaskUI.Loader. */
export function MarketingPageGeneratingPanelRow({ task }: { task?: AsyncTask }) {
  const label = task ? getTaskStatusMessage(task) : "Publishing page…";

  return (
    <div
      className="rounded-lg border border-dashed border-primary/30 bg-primary/5 p-3"
      aria-busy
      aria-label={label}
    >
      <div className="flex items-center gap-2 mb-2">
        <Loader2 className="size-3 shrink-0 animate-spin text-primary/70" aria-hidden />
        <span className="text-[13px] font-medium text-foreground/85">{label}</span>
      </div>
      <div className="space-y-1.5 pl-5">
        <div className="h-2 max-w-[180px] w-[75%] animate-pulse rounded bg-muted" />
        <div className="h-2 max-w-[100px] w-[45%] animate-pulse rounded bg-muted/70" />
      </div>
      <div className="mt-2.5 flex items-center gap-3 pl-5">
        <div className="h-2 w-14 animate-pulse rounded bg-muted/50" />
        <div className="h-2 w-12 animate-pulse rounded bg-muted/40 ml-auto" />
      </div>
    </div>
  );
}

/** In-card preview skeleton on Work Files grid while a page is generating. */
export function MarketingPageGeneratingWorkFilePreview({ task }: { task?: AsyncTask }) {
  const label = task ? getTaskStatusMessage(task) : "Publishing page…";

  return (
    <div
      className="mt-auto flex h-20 flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-primary/30 bg-primary/5 px-3 text-center"
      aria-busy
      aria-label={label}
    >
      <Loader2 className="size-3.5 shrink-0 animate-spin text-primary/70" aria-hidden />
      <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
    </div>
  );
}

/** Card skeleton for Marketing Pages grid — driven by AsyncTaskUI.Loader. */
export function MarketingPageGeneratingCard({ task }: { task?: AsyncTask }) {
  const label = task ? getTaskStatusMessage(task) : "Publishing page…";

  return (
    <article
      className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-dashed border-primary/30 bg-card/60 shadow-sm"
      aria-busy
      aria-label={label}
    >
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-center gap-2">
          <Loader2 className="size-3.5 shrink-0 animate-spin text-primary/70" aria-hidden />
          <span className="text-xs font-medium text-muted-foreground">{label}</span>
        </div>
        <div className="h-4 w-16 animate-pulse rounded-md bg-muted/80" />
        <div className="h-4 w-[70%] animate-pulse rounded-md bg-muted" />
        <div className="min-h-[2.5rem] rounded-md border border-border/40 bg-muted/30 p-2.5">
          <div className="mb-2 flex h-1.5 items-center gap-1 rounded-full bg-muted/80 px-2">
            {[0, 1, 2].map((i) => (
              <span key={i} className="size-1 rounded-full bg-muted-foreground/30" />
            ))}
          </div>
          <div className="space-y-1.5">
            <div className="h-2 w-[85%] animate-pulse rounded bg-muted/70" />
            <div className="h-2 w-[55%] animate-pulse rounded bg-muted/50" />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 border-t border-border/50 bg-muted/10 px-4 py-3">
        <div className="h-2 w-14 animate-pulse rounded bg-muted/50" />
        <div className="h-2 w-16 animate-pulse rounded bg-muted/40" />
      </div>
    </article>
  );
}
